import React, { useState } from 'react';
import { Form, Input, Select, Radio, Upload, Button, InputNumber, message } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import styles from './RefundOnly.module.scss';
import FAQSection from '../OrderServicePanel/FAQSection';
import StepBar from './StepBar';
import RefundStep2 from './RefundStep2';
import RefundStep3 from './RefundStep3';
import BuyerReturnStep from './BuyerReturnStep';
import { saveApplyAfterSale } from '@/api/order';
import { useAppSelector } from '@/store/hooks';

interface Props {
  order: any;
  onBack: () => void;
  onSubmit?: (form: any) => void;
  serviceType: 'refundOnly' | 'refundAndReturn';
  orderDetail?: any; // 可选：订单详情（来自 getOrderById）
}

const refundReasons = [
  { label: 'Overordered', value: 'Overordered' },
  { label: 'Poor product quality', value: 'Poor product quality' },
  { label: 'Item not as described', value: 'Item not as described' },
  { label: 'Counterfeit brand', value: 'Counterfeit brand' },
  { label: 'Damaged item', value: 'Damaged item' },
  { label: 'Other', value: 'Other' },
];

// 移除内联 BuyerReturnStep 组件实现，直接调用引入的组件

const RefundOnly: React.FC<Props> = ({ order, onBack, onSubmit, serviceType, orderDetail }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  
  // 获取token
  const token = useAppSelector((state) => state.user.token);
  
  // 图片上传配置
  const uploadProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".png,.jpeg,.jpg,.gif",
    headers: {
      token: `${token}`,
    },
    listType: 'picture-card',
    maxCount: 5,
    multiple: true,
    disabled: false,  // 不禁用整个组件，让删除功能始终可用
    showUploadList: {
      showPreviewIcon: false,  // 隐藏预览图标
      showRemoveIcon: true,    // 显示删除图标
      showDownloadIcon: false, // 隐藏下载图标
    },
    fileList: fileList,
   
    onRemove: (file) => {
      // 处理删除文件
      const newFileList = fileList.filter(item => item.uid !== file.uid);
      setFileList(newFileList);
      return true; // 返回true表示允许删除
    },
    onChange(info) {
      const newFileList = [...info.fileList];

      newFileList.forEach(file => {
        if (file.response) {
          file.url = file?.response?.data?.url;
        }
      });

      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("Image must be smaller than 10MB!");
        return false; // 阻止上传
      }
      // 在上传前检查文件数量，如果已达到最大数量则阻止上传
      if (fileList.length >= 5) {
        message.warning('You can only upload up to 5 images.');
        return false; // 阻止上传
      }
      return true; // 允许上传
    }
  };
  
  // 优先使用接口返回的详情数据
  const firstOrderItem = orderDetail?.order_items?.[0];
  const productFromDetail = firstOrderItem ? {
    img: firstOrderItem.product?.product_media_list?.[0]?.product_media_file_url || '',
    title: firstOrderItem.product_item_title,
    price: Number(firstOrderItem.product_item_price || 0),
    productNumber: firstOrderItem.product?.product_id,
    seller: firstOrderItem.product?.product_category_brand?.brand_name || firstOrderItem.product?.user?.username || '',
  } : null;
  const product = productFromDetail || (order.products[0] || {img: '', title: '', price: ''});
  const orderTotalPrice = orderDetail?.order?.order_total_price || product?.totalPrice || product?.price || 0;
  const unitPrice = Number(product?.price || 0);
  const shippingFee = 0;
  const totalPriceCalc = unitPrice + shippingFee;

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      // 验证必填字段
      const requiredFields = ['serviceType', 'goodsState', 'refundReason', 'refundAmount'];
      const missingFields = requiredFields.filter(field => !values[field]);
      
      if (missingFields.length > 0) {
        message.error('Please fill in all required fields');
        return;
      }

      // 验证退款金额
      const refundAmountNumber = typeof values.refundAmount === 'number'
        ? values.refundAmount
        : Number(String(values.refundAmount).replace(/[^0-9.]/g, ''));
      if (refundAmountNumber > product?.price) {
        message.error(`Refund amount cannot exceed $${product?.price}`);
        return;
      }

      // 处理图片数据格式
      const formattedImages = fileList.map(file => ({
        product_id: null,
        product_media_type: "images",
        product_media_file_url: file.url || '',
        product_media_video_cover_id: null,
        file_id: file.uid,
        product_media_id: null
      }));

      // 组装提交参数
      const orderId = orderDetail?.order?.order_id || order?.id;
      const payload = {
        order_id: orderId,
        service_type: values.serviceType, // refund | return
        goods_status: values.goodsState, // not_received | received
        refund_reason: values.refundReason,
        refund_amount: refundAmountNumber,
        extension: values.description || '',
        images: formattedImages, // 添加图片数据
      };

      // 提交售后申请
      const res: any = await saveApplyAfterSale(payload);
      if (res?.code !== 200) {
        message.error(res?.message || 'Failed to submit after-sale application');
        return;
      }

      // 保存表单数据
      setFormData(values);

      // 调用父组件的 onSubmit（如有）
      if (onSubmit) {
        await onSubmit({ 
          ...values, 
          file: values.upload?.file?.originFileObj || null 
        });
      }

      // 跳转到第二步
      setCurrentStep(2);
      message.success('Form submitted successfully!');
      
      // 清理文件列表
      setFileList([]);
      
    } catch (error) {
      message.error('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFormFinish = (values: any) => {
    handleSubmit(values);
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
  };

  const handleNextToStep3 = () => {
    setCurrentStep(3);
  };

  // 步骤数据
  const stepData = serviceType === 'refundAndReturn'
    ? [
        "When A Buyer Applies For A Refund",
        "Sellers Handle Refund Requests",
        "Buyer Return",
        "The Refund Has Been Completed",
      ]
    : [
        "When A Buyer Applies For A Refund",
        "Sellers Handle Refund Requests",
        "The Refund Has Been Completed",
      ];

  // 四步流程时插入 BuyerReturnStep 组件
  if (serviceType === 'refundAndReturn' && currentStep === 3) {
    return (
      <BuyerReturnStep
        formData={formData}
        order={order}
        onBack={() => setCurrentStep(2)}
        onNext={() => setCurrentStep(4)}
        serviceType={serviceType}
        stepData={stepData}
        currentStep={3}
      />
    );
  }
  if (
    (serviceType === 'refundOnly' && currentStep === 3) ||
    (serviceType === 'refundAndReturn' && currentStep === 4)
  ) {
    return (
      <RefundStep3
        formData={formData}
        order={order}
        stepData={stepData}
        currentStep={serviceType === 'refundAndReturn' ? 4 : 3}
      />
    );
  }
  // 第二步组件
  if (currentStep === 2) {
    return (
      <RefundStep2 
        formData={formData}
        onBack={handleBackToStep1}
        onNext={handleNextToStep3}
        serviceType={serviceType}
        stepData={stepData}
        currentStep={2}
      />
    );
  }
  // 渲染第一步表单
  return (
    <div>
      <StepBar currentStep={currentStep} stepData={stepData} />
      <div className={styles['main-content']}>
        <div className={styles['form-box']}>
        <Form
          form={form}
          layout="horizontal"
          className={styles['refund-form']}
          labelCol={{ flex: '140px' }}
          wrapperCol={{ flex: 1 }}
          colon={false}
          initialValues={{
            serviceType: 'refund',
            goodsState: serviceType === 'refundAndReturn' ? 'received' : 'not_received',
            refundReason: undefined,
            refundAmount: product?.price,
            description: '',
          }}
          onFinish={handleFormFinish}
        >
          <Form.Item
            label={<span className={styles['label-span']}>Refunded products: <span className={styles['required']}></span></span>}
            className={styles['form-section']}
          >
            <div className={styles['product-header']}>
              <img className={styles['product-img']} src={product?.img} alt={product?.title} />
              <div className={styles['product-info']}>
                <div className={styles['product-title']}>{product?.title}</div>
                <div className={styles['product-classification']}>Shape Classification: Square Handheld Wicker Basket</div>
                <div className={styles['product-price']}>${product?.price}</div>
              </div>
            </div>
          </Form.Item>

          <Form.Item
            label={<span className={styles['label-span']}>Service Types: <span className={styles['required']}>*</span></span>}
            name="serviceType"
            rules={[{ required: true, message: 'Please select service type' }]}
            className={styles['form-section']}
          >
              <Radio.Group className={styles['custom-radio-group']}>
                <Radio value="refund">Refund</Radio>
                <Radio value="return">Return and Refund</Radio>
              </Radio.Group>
          </Form.Item>

          <Form.Item
            label={<span  className={styles['label-span']}>State of goods: <span className={styles['required']}>*</span></span>}
            name="goodsState"
            rules={[{ required: true, message: 'Please select state of goods' }]}
            className={styles['form-section']}
          >
      
              <Radio.Group className={styles['custom-radio-group']}>
                <Radio value="not_received" disabled={serviceType === 'refundAndReturn'}>Haven't received the goods</Radio>
                <Radio value="received">Have received the goods</Radio>
              </Radio.Group>
          </Form.Item>

          <Form.Item
            label={<span className={styles['label-span']}>Refund reason: <span className={styles['required']}>*</span></span>}
            name="refundReason"
            rules={[{ required: true, message: 'Please select refund reason' }]}
            className={styles['form-section']}
          >
              <Select
                className={styles['custom-select']}
                placeholder="Please Select"
                popupClassName={styles['custom-select-dropdown']}
              >
                {refundReasons.map(r => (
                  <Select.Option key={r.value} value={r.value}>{r.label}</Select.Option>
                ))}
              </Select>
          </Form.Item>
          <Form.Item
              label={<span className={styles['label-span']}>Requested refund amount: <span className={styles['required']}>*</span></span>}
              name="refundAmount"
              rules={[
                { required: true, message: 'Please input refund amount' },
                { 
                  validator: (_, value) => {
                    if (value > product?.price) {
                      return Promise.reject(new Error(`Amount cannot exceed $${product?.price}`));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              className={styles['form-section']}
            >
              <div className={styles['form-item-flex']}>
                <div className={styles['form-input-tip']}>
                  <InputNumber
                    className={styles['custom-input']}
                    min={0}
                    max={product?.price}
                    step={1}
                    formatter={value => `$ ${value}`}
                    parser={value => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      // 确保输入值不超过商品价格
                      if (value && value > product?.price) {
                        form.setFieldsValue({ refundAmount: product?.price });
                      }
                    }}
                  />
                  <div className={styles['form-tip']}>The Maximum Product Amount That Can Be Entered Is ${product?.price}</div>
                </div>
              </div>
            </Form.Item>

          <Form.Item
            label={<span className={styles['label-span']}>Additional description:<span className={styles['required']}></span></span>}
            name="description"
            className={styles['form-section'] + ' ' + styles['desc-section']}
          >
              <Input.TextArea
                className={styles['custom-textarea']}
                maxLength={300}
                placeholder="Additional Description"
                showCount
              />
          </Form.Item>

          <Form.Item
            label={<span className={styles['label-span']}>Upload:<span className={styles['required']}></span></span>}
            name="images"
            className={styles['form-section']}
          >
              <Upload {...uploadProps} className={styles['custom-upload-box']}>
                {fileList.length >= 5 ? null : (
                  <Button
                    className={styles['ant-btn']}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                    }}
                    icon={<UploadOutlined />}
                  >
                    <span>Upload <br /> (Optional)</span>
                  </Button>
                )}
              </Upload>
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 1 }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              className={styles['custom-submit']}
              loading={loading}
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
        {/* 

 */}
        </div>
        <div className={styles['order-detail']}>
          <div className={styles['oriduct-title']}>
             Order Details
          </div>
          <div className={styles['product-header']}>
            <img className={styles['product-img']} src={product?.img} alt={product?.title} />
            <div className={styles['product-info']}>
              <div className={styles['product-title']}>{product?.title}</div>
              <div className={styles['product-titles']}>Shape Classification...</div>
              {/* <div className={styles['product-price']}>${product?.price}</div> */}
            </div>
          </div>
          <div className={styles['product-info-list']}>
            <div><span>Seller:</span> {product?.seller}</div>
            <div><span>Product Number:</span> {product?.productNumber}</div>
            <div><span>Unit Price:</span> ${unitPrice}</div>
            <div><span>Shipping Fee:</span> ${shippingFee}</div>
            <div><span>Total Price:</span> ${totalPriceCalc}</div>
          </div>
        </div>
      </div>
      <FAQSection />
    </div>
  );
};

export default RefundOnly; 
